import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Typography,
  LinearProgress,
  CircularProgress,
  Alert,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

ProcessingStep.propTypes = {
  uploadProgress: PropTypes.object.isRequired,
  processingStatus: PropTypes.object,
  onCheckProgress: PropTypes.func,
}

export default function ProcessingStep({
  uploadProgress,
  processingStatus,
  onCheckProgress,
}) {
  const { t } = useTranslation()
  const isPausedRef = React.useRef(false)
  const retryCountRef = React.useRef(0)
  const pauseTimeoutRef = React.useRef(null)
  const onCheckProgressRef = React.useRef(onCheckProgress)

  const { uploaded = 0, total = 0 } = uploadProgress
  const uploadPercentage = total > 0 ? Math.round((uploaded / total) * 100) : 0
  const isUploading = uploaded < total
  const isProcessing = !isUploading && processingStatus

  // Keep the callback ref up to date
  useEffect(() => {
    onCheckProgressRef.current = onCheckProgress
  }, [onCheckProgress])

  // Poll for progress updates if handler is provided
  useEffect(() => {
    console.log('[ProcessingStep] Polling check:', {
      isUploading,
      uploaded,
      total,
      hasProcessingStatus: !!processingStatus,
      isProcessing,
      hasOnCheckProgress: !!onCheckProgress,
      shouldPoll: isProcessing && !!onCheckProgress,
    })

    if (!isProcessing || !onCheckProgress) return

    console.log('[ProcessingStep] Starting polling every 1 second')
    const interval = setInterval(async () => {
      // Skip if paused
      if (isPausedRef.current) {
        console.log('[ProcessingStep] Skipping poll - paused for token refresh')
        return
      }

      console.log('[ProcessingStep] Polling...')

      const timeoutMs = 3000 // 3 second timeout to detect queued requests
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('REQUEST_TIMEOUT')), timeoutMs)
      })

      try {
        // Race between the actual request and timeout
        await Promise.race([onCheckProgressRef.current(), timeoutPromise])

        // Reset retry count on successful poll
        if (retryCountRef.current > 0) {
          console.log(
            '[ProcessingStep] Request succeeded - resuming normal polling'
          )
          retryCountRef.current = 0
        }
      } catch (error) {
        // Check if request is hanging (likely queued by auth retrier)
        if (error?.message === 'REQUEST_TIMEOUT') {
          console.log(
            '[ProcessingStep] Request timeout detected - likely queued for token refresh, pausing polling'
          )
          isPausedRef.current = true
          retryCountRef.current += 1

          // Clear any existing pause timeout
          if (pauseTimeoutRef.current) {
            clearTimeout(pauseTimeoutRef.current)
          }

          // Wait before resuming (exponential backoff)
          const backoffMs = Math.min(
            2000 * 2 ** (retryCountRef.current - 1),
            10000
          )
          console.log(
            `[ProcessingStep] Waiting ${backoffMs}ms before resuming polling`
          )

          pauseTimeoutRef.current = setTimeout(() => {
            console.log(
              '[ProcessingStep] Resuming polling after token refresh wait'
            )
            isPausedRef.current = false
            pauseTimeoutRef.current = null
          }, backoffMs)
        } else if (error?.response?.status === 401) {
          // Direct 401 error (shouldn't happen with auth retrier, but just in case)
          console.log('[ProcessingStep] 401 detected - pausing polling')
          isPausedRef.current = true
        } else {
          console.error('[ProcessingStep] Polling error:', error)
        }
      }
    }, 1000) // Poll every 1 second

    // eslint-disable-next-line consistent-return
    return () => {
      console.log('[ProcessingStep] Stopping polling')
      clearInterval(interval)
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessing])

  return (
    <Box sx={{ py: 4 }}>
      {isUploading && (
        <Box>
          <Typography variant="h6" gutterBottom>
            {t(
              'components.multi-document-import-dialog.uploading-files',
              'Uploading Files...'
            )}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={uploadPercentage}
            sx={{ mt: 2, mb: 1 }}
          />
          <Typography variant="body2" color="textSecondary" align="center">
            {uploaded} / {total} {t('common.files', 'files')} (
            {uploadPercentage}%)
          </Typography>
        </Box>
      )}

      {isProcessing && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <CircularProgress />
          </Box>
          <Typography variant="h6" align="center" gutterBottom>
            {t(
              'components.multi-document-import-dialog.processing-documents',
              'Processing Documents...'
            )}
          </Typography>
          <Typography variant="body2" color="textSecondary" align="center">
            {t(
              'components.multi-document-import-dialog.processing-description',
              'This may take several minutes. You can close this dialog and the processing will continue in the background.'
            )}
          </Typography>

          {processingStatus.estimatedTime && (
            <Alert severity="info" sx={{ mt: 3 }}>
              {processingStatus.estimatedTime === 1
                ? t(
                    'components.multi-document-import-dialog.estimated-time-one',
                    {
                      count: processingStatus.estimatedTime,
                      defaultValue: 'Estimated time: {{count}} minute',
                    }
                  )
                : t(
                    'components.multi-document-import-dialog.estimated-time-other',
                    {
                      count: processingStatus.estimatedTime,
                      defaultValue: 'Estimated time: {{count}} minutes',
                    }
                  )}
            </Alert>
          )}
        </Box>
      )}
    </Box>
  )
}
